import React, { useState } from 'react';
import { ChevronDown, History, ExternalLink, Calendar, CalendarDays } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { useCompanyPricingHistory, groupEventsByCategory, type PricingEvent, type QuarterData } from '@/hooks/useCompanyPricingHistory';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Link } from 'react-router-dom';

type PeriodTab = 'quarters' | 'weeks';

// Category styling configuration
const CATEGORY_CONFIG: Record<string, { label: string; bg: string; text: string; dot: string }> = {
  pricing_structure: {
    label: 'Pricing Structure',
    bg: 'bg-blue-50',
    text: 'text-blue-700',
    dot: 'bg-blue-500'
  },
  feature: {
    label: 'Feature Change',
    bg: 'bg-purple-50',
    text: 'text-purple-700',
    dot: 'bg-purple-500'
  },
  limits: {
    label: 'Limits',
    bg: 'bg-green-50',
    text: 'text-green-700',
    dot: 'bg-green-500'
  },
  misc: {
    label: 'Misc',
    bg: 'bg-gray-50',
    text: 'text-gray-700',
    dot: 'bg-gray-500'
  }
};

interface PricingHistoryTimelineProps {
  companySlug: string;
}

export const PricingHistoryTimeline: React.FC<PricingHistoryTimelineProps> = ({
  companySlug,
}) => {
  const { events, quarterData, weekData, loading, error } = useCompanyPricingHistory(companySlug);
  const [expandedQuarters, setExpandedQuarters] = useState<Set<string>>(new Set());
  const [activeTab, setActiveTab] = useState<PeriodTab>('quarters');

  const toggleQuarter = (period: string) => {
    setExpandedQuarters(prev => {
      const next = new Set(prev);
      if (next.has(period)) {
        next.delete(period);
      } else {
        next.add(period);
      }
      return next;
    });
  };

  // Get category config with fallback
  const getCategoryConfig = (category: string) => {
    return CATEGORY_CONFIG[category] || CATEGORY_CONFIG.misc;
  };

  if (loading) {
    return <TimelineSkeleton />;
  }

  if (error) {
    return (
      <div className="text-sm text-red-500 py-2">
        Error loading pricing history: {error}
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <div className="text-sm text-gray-500 py-2 text-center">
        No pricing changes recorded for this page
      </div>
    );
  }

  const currentData = activeTab === 'quarters' ? quarterData : weekData;
  const quarterCount = quarterData.reduce((sum, q) => sum + q.events.length, 0);
  const weekCount = weekData.reduce((sum, w) => sum + w.events.length, 0);

  return (
    <div className="space-y-2">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <History className="h-4 w-4 text-gray-500" />
          <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
            Pricing History
          </span>
          <Badge variant="secondary" className="text-xs">
            {events.length} {events.length === 1 ? 'change' : 'changes'}
          </Badge>
        </div>
        <Link
          to={`/diffs?slug=${companySlug}`}
          className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
        >
          View all
          <ExternalLink className="h-3 w-3" />
        </Link>
      </div>

      {/* Tab Switcher */}
      <div className="flex gap-1 p-1 bg-gray-100 rounded-lg w-fit">
        <button
          onClick={() => setActiveTab('quarters')}
          className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
            activeTab === 'quarters'
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <Calendar className="h-3.5 w-3.5" />
          Quarters
          {quarterCount > 0 && (
            <span className={`ml-1 ${activeTab === 'quarters' ? 'text-gray-500' : 'text-gray-400'}`}>
              ({quarterCount})
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('weeks')}
          className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
            activeTab === 'weeks'
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <CalendarDays className="h-3.5 w-3.5" />
          Weeks
          {weekCount > 0 && (
            <span className={`ml-1 ${activeTab === 'weeks' ? 'text-gray-500' : 'text-gray-400'}`}>
              ({weekCount})
            </span>
          )}
        </button>
      </div>

      {/* Timeline */}
      {currentData.length === 0 ? (
        <div className="text-sm text-gray-400 py-4 text-center border rounded-lg">
          No {activeTab === 'quarters' ? 'quarterly' : 'weekly'} pricing changes recorded
        </div>
      ) : (
        <div className="border rounded-lg divide-y">
          {currentData.map((quarter) => (
            <QuarterSection
              key={quarter.period}
              quarter={quarter}
              isExpanded={expandedQuarters.has(quarter.period)}
              onToggle={() => toggleQuarter(quarter.period)}
              getCategoryConfig={getCategoryConfig}
              companySlug={companySlug}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// Quarter section component
interface QuarterSectionProps {
  quarter: QuarterData;
  isExpanded: boolean;
  onToggle: () => void;
  getCategoryConfig: (category: string) => typeof CATEGORY_CONFIG[string];
  companySlug: string;
}

const QuarterSection: React.FC<QuarterSectionProps> = ({
  quarter,
  isExpanded,
  onToggle,
  getCategoryConfig,
  companySlug,
}) => {
  const categoryGroups = groupEventsByCategory(quarter.events);

  // Get the first event's versions for the links
  const firstEvent = quarter.events[0];
  const viewUrl = firstEvent?.v_after && firstEvent?.v_before
    ? buildPricingSaasUrl(companySlug, firstEvent.v_after, firstEvent.v_before)
    : null;
  const editUrl = firstEvent?.news_id ? `/diffs/${firstEvent.news_id}/edit` : null;

  return (
    <Collapsible open={isExpanded} onOpenChange={onToggle}>
      <div className="flex items-center">
        <CollapsibleTrigger className="flex-1 px-3 py-2 flex items-center justify-between hover:bg-gray-50 transition-colors">
          <div className="flex items-center gap-2">
            <ChevronDown
              className={`h-4 w-4 text-gray-400 transition-transform ${isExpanded ? '' : '-rotate-90'}`}
            />
            <span className="text-sm font-medium text-gray-700">{quarter.quarter}</span>
            <span className="text-xs text-gray-400">
              ({quarter.events.length} {quarter.events.length === 1 ? 'event' : 'events'})
            </span>
          </div>
          <div className="flex items-center gap-1">
            {categoryGroups.map(({ category }) => {
              const config = getCategoryConfig(category);
              return (
                <div
                  key={category}
                  className={`w-2 h-2 rounded-full ${config.dot}`}
                  title={config.label}
                />
              );
            })}
          </div>
        </CollapsibleTrigger>
        <div className="flex items-center gap-1 pr-2">
          {viewUrl && (
            <a
              href={viewUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="px-2 py-1 text-xs text-blue-600 hover:text-blue-800 font-medium hover:bg-blue-50 rounded transition-colors"
            >
              View
            </a>
          )}
          {editUrl && (
            <Link
              to={editUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="px-2 py-1 text-xs text-gray-600 hover:text-gray-800 font-medium hover:bg-gray-100 rounded transition-colors"
            >
              Edit
            </Link>
          )}
        </div>
      </div>

      <CollapsibleContent>
        <div className="pl-6 pr-10 pb-2 space-y-0.5">
          {[...quarter.events].sort((a, b) => {
            const priority: Record<string, number> = {
              pricing_structure: 1,
              feature: 2,
              limits: 3,
              misc: 4,
            };
            const pA = priority[a.category || 'misc'] || 999;
            const pB = priority[b.category || 'misc'] || 999;
            return pA - pB;
          }).map((event) => (
            <EventRow
              key={event.id}
              event={event}
              getCategoryConfig={getCategoryConfig}
            />
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};

// Individual event row with bullet and category pill
interface EventRowProps {
  event: PricingEvent;
  getCategoryConfig: (category: string) => typeof CATEGORY_CONFIG[string];
}

// Build external PricingSaaS diff viewer URL
const buildPricingSaasUrl = (slug: string, vAfter: string, vBefore: string): string => {
  return `https://pricingsaas.com/news/${slug}/${vAfter}?v_before=${vBefore}&v3=true`;
};

const EventRow: React.FC<EventRowProps> = ({ event, getCategoryConfig }) => {
  // Use labels (short) first, then fall back to description (long)
  const label = event.labels?.[0] || event.event_description || 'Pricing change';
  const category = event.category || 'misc';
  const config = getCategoryConfig(category);

  return (
    <div className="flex items-center gap-2 py-0.5">
      <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${config.dot}`} />
      <span className="text-sm text-gray-700">{label}</span>
      <span className={`text-[10px] px-1.5 py-0.5 rounded ${config.bg} ${config.text} flex-shrink-0`}>
        {config.label}
      </span>
    </div>
  );
};

// Loading skeleton
const TimelineSkeleton: React.FC = () => {
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Skeleton className="h-4 w-4" />
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-5 w-16 rounded-full" />
      </div>
      <div className="border rounded-lg divide-y">
        {[1, 2].map((i) => (
          <div key={i} className="px-3 py-2">
            <div className="flex items-center gap-2">
              <Skeleton className="h-4 w-4" />
              <Skeleton className="h-4 w-20" />
              <Skeleton className="h-3 w-12" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PricingHistoryTimeline;
